import React from 'react';
import { useMediaQuery } from 'react-responsive';
import classNameBind from 'classnames/bind';
import { useTranslation } from '../../../Translator';
import Spinner from '../../../Spinner';
import Skeleton from '../../../Skeleton';
import { TrafficUsageProgressBar } from '../TrafficUsageProgressBar';
import ActionList from '../../../ActionList';
import Button from '../../../Button';
import Caption from '../../../Caption';
import styles from './Menu.module.css';
import { UserInfo } from './UserInfo';
import Popover from '../../../Popover';
import { TrafficUsageIndicator } from '../TrafficUsageIndicator';

const PPAS_VERSION_CHANGELOG_HREF = 'https://piwik.pro/changelog/';
const bindClasses = classNameBind.bind(styles);

const Menu = ({
  ppasVersion,
  email,
  menuItemsError,
  itemsLoaded,
  productMenuItems = [],
  contextMenuItems = [],
  onLogout,
  profileLink,
  trafficLoading,
  trafficError,
  traffic,
  trafficLimit,
  userRole,
  licenseType,
  licenseError,
}) => {
  const { t } = useTranslation('components');
  const trafficUsage = Math.floor((traffic / trafficLimit) * 100);
  const isMobile = useMediaQuery({ query: '(max-width: 960px)' });
  const clickable = userRole === 'OWNER';

  const getTrafficStatusVisibility = () => {
    if (trafficLimit <= -1) {
      return false;
    }

    if (licenseType === 'core') {
      return true;
    }

    return userRole === 'OWNER';
  };

  const getProgressBarColor = (progress) => {
    if (progress >= 100) return 'red';
    if (progress >= 80) return 'orange';

    return 'blue';
  };

  const getIndicatorState = (progress) => {
    if (progress >= 100) return 'alert';
    if (progress >= 80) return 'warning';

    return 'none';
  };

  const getLinksList = () => (
    <>
      <div className={bindClasses('menu-section-products')}>
        <ActionList
          groups={[{
            id: 1,
            items: productMenuItems.map((el, index) => ({ ...el, id: index })),
          }]}
        />
      </div>
      <div className={bindClasses('menu-section-common')}>
        <ActionList
          groups={[{
            id: 1,
            items: contextMenuItems.map((el, index) => ({ ...el, id: index })),
          }]}
        />
      </div>
      <div className={bindClasses('menu-section-user')}>
        <UserInfo profileLink={profileLink} email={email} />
        {
          getTrafficStatusVisibility() && (
            <TrafficUsageProgressBar
              clickable={clickable}
              error={trafficError || licenseError}
              loading={trafficLoading}
              progress={trafficUsage}
              color={getProgressBarColor(trafficUsage)}
            />
          )
        }
        <div className={bindClasses('menu-section-products')}>
          <Button
            size="small"
            fullWidth
            text={t('linkNameLogout')}
            onClick={onLogout}
          />
        </div>
        <div className={bindClasses('ppas-version-wrapper')}>
          <a
            className={bindClasses('changelog-link')}
            href={PPAS_VERSION_CHANGELOG_HREF}
            target="_blank"
            rel="noreferrer"
          >
            <div className={bindClasses('inner')}>
              {
                !ppasVersion
                  ? <Skeleton height={8} width={70} />
                  : (<span>{ `${t('version')} ${ppasVersion}`}</span>)
              }

            </div>
          </a>
        </div>
      </div>

    </>
  );

  const getLoadStatus = () => (
    <div className={bindClasses('load-status')}>
      {menuItemsError ? (
        <Caption subdued>{t('productLinksLoadError')}</Caption>
      ) : (
        <Spinner className="small" />
      )}
    </div>
  );

  return (
    <Popover
      position="bottom-start"
      dataId="ProductBar.menu"
      noPadding
      activatorElement={(
        <div className={bindClasses('button-container')}>
          <Button
            icon="applications"
            appearance="default"
            text={isMobile ? undefined : t('menu')}
          />
          {
            getTrafficStatusVisibility() && !licenseError && <TrafficUsageIndicator status={getIndicatorState(trafficUsage)} />
          }
        </div>
      )}
    >
      <div
        className={bindClasses('wrapper')}
        data-ppuc-id="ProductBar.Menu"
      >
        {(itemsLoaded && !menuItemsError)
          ? getLinksList()
          : getLoadStatus()
        }
      </div>
    </Popover>
  );
};

export { Menu };
